<template>
    <div>
        <PageHeader>
            {{ $t('yourTipboxLink') }}
            <template #side>
                <div class="d-none d-md-flex flex-wrap gap-3">
                    <ButtonIcon
                        v-if="landingPage"
                        :href="profileUrl"
                        target="_blank"
                        variant="success"
                        icon="external-link"
                        flip-icon
                        :disabled="busy"
                    >
                        {{ $t('seeYourProfile') }}
                    </ButtonIcon>
                    <ButtonIcon :to="parentRoute">
                        {{ $t('goBack') }}
                    </ButtonIcon>
                </div>
            </template>
            <template #bottom>
                <div class="d-md-none d-flex flex-wrap gap-3 mb-3">
                    <ButtonIcon
                        v-if="landingPage"
                        :href="profileUrl"
                        target="_blank"
                        variant="success"
                        icon="external-link"
                        flip-icon
                        :disabled="busy"
                    >
                        {{ $t('seeYourProfile') }}
                    </ButtonIcon>
                    <ButtonIcon :to="parentRoute">
                        {{ $t('goBack') }}
                    </ButtonIcon>
                </div>

                <b-button href="https://getbuybox.com/ustawienia-twojego-profilu-tipbox-link/" target="_blank" variant="link" class="d-flex align-items-center p-0 mr-4">
                    <feather type="file-text" class="mr-2" /> {{ $t('seeHowToUseTipboxLink') }}
                </b-button>
            </template>
        </PageHeader>

        <Card :header="$t('settingsYourProfile')" :sub-header="$t('settingsYourProfileDescription')" class="mb-0 shadow-none" overlay :loading="loading">
            <div class="row gx-4 gy-4">
                <div class="col-12 col-md-6">
                    <FormValidator id="form-group-profile-name" :validator="$v.form.profileName" v-slot="slotProps" form-group :label="$t('profileName')">
                        <b-form-input id="form-profile-name" v-model="form.profileName" :state="slotProps.state" :disabled="busy" />
                    </FormValidator>

                    <FormValidator id="form-group-description" :validator="$v.form.description" :msg-props="{ max: $v.form.description.$params.maxLength.max }" form-group>
                        <template #label>
                            <div class="d-flex align-items-center">
                                <div>{{ $t('profileDescription') }}</div>
                                <div class="ml-auto text-secondary font-weight-normal">{{ form.description.length }}/{{ $v.form.description.$params.maxLength.max }}</div>
                            </div>
                        </template>
                        <template #default="slotProps">
                            <b-form-textarea
                                id="form-description"
                                v-model="form.description"
                                :state="slotProps.state"
                                class=""
                                :disabled="busy"
                                rows="4"
                            />
                        </template>
                    </FormValidator>

                    <FormValidator id="form-group-thanks" :validator="$v.form.thanks" :msg-props="{ max: $v.form.thanks.$params.maxLength.max }" form-group>
                        <template #label>
                            <div class="d-flex align-items-center">
                                <div>{{ $t('contentOfThanks') }}</div>
                                <div class="ml-auto text-secondary font-weight-normal">{{ form.thanks.length }}/{{ $v.form.thanks.$params.maxLength.max }}</div>
                            </div>
                        </template>
                        <template #default="slotProps">
                            <b-form-textarea
                                id="form-thanks"
                                v-model="form.thanks"
                                :state="slotProps.state"
                                class=""
                                :disabled="busy"
                                rows="4"
                            />
                        </template>
                    </FormValidator>

                    <b-form-group id="form-group-amounts" :label="$t('amounts')">
                        <div class="d-flex flex-wrap gap-2">
                            <FormValidator
                                id="form-group-amounts-0"
                                :validator="$v.form.amounts[0]"
                                :msg-props="{ min: amounts.min[0], max: amounts.max[0] }"
                                v-slot="slotProps"
                                class="flex-grow-1 flex-basis-0 width-min-50 width-max-200"
                            >
                                <b-form-input id="form-amounts-0" v-model="form.amounts[0]" :state="slotProps.state" :disabled="busy" placeholder="5" />
                            </FormValidator>
                            <FormValidator
                                id="form-group-amounts-1"
                                :validator="$v.form.amounts[1]"
                                :msg-props="{ min: amounts.min[1], max: amounts.max[1] }"
                                v-slot="slotProps"
                                class="flex-grow-1 flex-basis-0 width-min-50 width-max-200"
                            >
                                <b-form-input id="form-amounts-1" v-model="form.amounts[1]" :state="slotProps.state" :disabled="busy" placeholder="10" />
                            </FormValidator>
                            <FormValidator
                                id="form-group-amounts-2"
                                :validator="$v.form.amounts[2]"
                                :msg-props="{ min: amounts.min[2], max: amounts.max[2] }"
                                v-slot="slotProps"
                                class="flex-grow-1 flex-basis-0 width-min-50 width-max-200"
                            >
                                <b-form-input id="form-amounts-2" v-model="form.amounts[2]" :state="slotProps.state" :disabled="busy" placeholder="15" />
                            </FormValidator>
                        </div>
                    </b-form-group>

                    <b-form-group id="form-group-links" :label="$t('yourLinks')">
                        <div class="d-flex flex-column gap-3">
                            <div v-for="(item, index) in form.links" :key="index" class="d-flex gap-3">
                                <FormValidator
                                    :id="`form-group-links-${index}-url`"
                                    class="d-flex flex-column flex-grow-2 flex-shrink-0 flex-basis-0"
                                    :validator="$v.form[`links-${index}-url`]"
                                    v-slot="slotProps"
                                >
                                    <b-form-input
                                        :id="`form-links-${index}-url`"
                                        v-model="form.links[index].url"
                                        :placeholder="$t('urlAddress')"
                                        :disabled="busy"
                                        :state="slotProps.state"
                                    />
                                </FormValidator>
                                <FormValidator
                                    :id="`form-group-links-${index}-social`"
                                    class="d-flex flex-column flex-grow-1 flex-shrink-0 flex-basis-0"
                                    :validator="$v.form[`links-${index}-social`]"
                                    v-slot="slotProps"
                                >
                                    <b-form-select
                                        :id="`form-links-${index}-social`"
                                        v-model="form.links[index].social"
                                        :placeholder="$t('kind')"
                                        :disabled="busy"
                                        :state="slotProps.state"
                                        :options="socials"
                                        text-field="label"
                                        value-field="value"
                                    />
                                </FormValidator>
                                <ButtonIcon
                                    :disabled="busy"
                                    variant="danger"
                                    icon="trash-2"
                                    :title="$t('remove')"
                                    class="mb-auto"
                                    @click="removeLink(index)"
                                />
                            </div>
                            <ButtonIcon :disabled="busy" class="wide mr-auto" variant="outline-success" @click="addLink()">
                                {{ $t('addAnotherLink') }}
                            </ButtonIcon>
                        </div>
                    </b-form-group>
                    <FormValidator id="form-group-display-video" :validator="$v.form.displayVideo" v-slot="slotProps" form-group>
                        <div class="d-flex gap-3">
                            <b-form-checkbox id="form-display-video" v-model="form.displayVideo" :state="slotProps.state" class="my-auto" :disabled="busy">
                                {{ $t('displayVideo') }}
                            </b-form-checkbox>
                        </div>
                    </FormValidator>

                    <template v-if="form.displayVideo">
                        <FormValidator id="form-group-video" :label="$t('video')" :validator="$v.form.video" v-slot="slotProps" form-group>
                            <div class="d-flex gap-3">
                                <b-form-input
                                    id="form-video"
                                    v-model="form.video"
                                    :state="slotProps.state"
                                    placeholder="https://"
                                    class=""
                                    :disabled="busy"
                                />
                            </div>
                        </FormValidator>

                        <FormValidator id="form-group-thanks-video" :label="$t('thanksVideoOptional')" :validator="$v.form.thanksVideo" v-slot="slotProps" form-group>
                            <div class="d-flex gap-3">
                                <b-form-input
                                    id="form-thanks-video"
                                    v-model="form.thanksVideo"
                                    :state="slotProps.state"
                                    placeholder="https://"
                                    class=""
                                    :disabled="busy"
                                />
                            </div>
                        </FormValidator>
                    </template>
                </div>
                <div class="col-12 col-md-6">
                    <FormValidator id="form-group-slug" :validator="$v.form.slug" v-slot="slotProps" form-group :label="$t('linkToProfile')">
                        <b-input-group prepend="tipbox.link/">
                            <b-form-input id="form-slug" v-model="form.slug" :state="slotProps.state" :disabled="busy" :placeholder="$t('tipboxSlugPlaceholder')" />
                        </b-input-group>
                    </FormValidator>

                    <FormValidator
                        id="form-group-avatar"
                        class="d-flex flex-column flex-grow-1"
                        :validator="$v.formAvatar.avatar"
                        form-group
                        :label="$t('avatar')"
                        v-slot="slotProps"
                        :msg-props="{ max: $v.formAvatar.avatar.$params.fileSizeMax.max }"
                        :description="`${$t('supportedFormatsX', { formats: 'APNG, AVIF, GIF, JPEG, PNG, SVG, WEBP' })} ${$t('maximumFileSizeX', {
                            size: $v.formAvatar.avatar.$params.fileSizeMax.max,
                        })}`"
                    >
                        <div class="d-flex gap-2">
                            <FormFile
                                id="form-avatar"
                                v-model="formAvatar.avatar"
                                accept="image/*"
                                show-thumbnails
                                :state="slotProps.state"
                                :disabled="busy"
                                :loading="requestTipboxLandingPageAvatar.getLoading()"
                            />
                            <b-modal
                                v-if="landingPage"
                                ref="formAvatarModal"
                                centered
                                hide-footer
                                hide-header
                                body-class="text-center d-flex justify-content-center align-items-center height-min-200"
                            >
                                <ImageLoader v-if="landingPage" :src="landingPage.avatar" image-class="w-auto" error-class="w-auto" style="max-width: 100%" />
                            </b-modal>
                            <ImageLoader
                                v-if="landingPage"
                                :src="landingPage.avatar"
                                class="my-auto cursor-pointer"
                                height="40px"
                                width="40px"
                                @click.native="$refs.formAvatarModal.show()"
                            />
                        </div>
                    </FormValidator>

                    <b-form-group id="form-group-color" :label="$t('color')">
                        <ColorField id="form-color" v-model="form.color" placeholder="#000000" :disabled="busy" class="mb-0" />
                    </b-form-group>

                    <div class="d-flex flex-column">
                        <div class="d-flex flex-wrap gap-2">
                            <FormValidator
                                id="form-group-icon"
                                :validator="$v.form.icon"
                                v-slot="slotProps"
                                form-group
                                :label="$t('icon')"
                                class="flex-grow-1"
                            >
                                <b-form-select
                                    id="form-icon"
                                    v-model="form.icon"
                                    :options="optionsIcons"
                                    text-field="label"
                                    class="flex-grow-1"
                                    :placeholder="$t('select')"
                                    add-null-option
                                    :state="slotProps.state"
                                    :disabled="busy"
                                />
                            </FormValidator>

                            <FormValidator
                                id="form-group-icon-version"
                                :validator="$v.form.iconVersion"
                                v-slot="slotProps"
                                form-group
                                :label="$t('kind')"
                                class="flex-grow-1"
                            >
                                <b-form-select
                                    id="form-icon-version"
                                    v-model="form.iconVersion"
                                    :options="optionsIconVersions"
                                    text-field="label"
                                    class="flex-grow-1"
                                    :placeholder="$t('select')"
                                    add-null-option
                                    :state="slotProps.state"
                                    :disabled="busy || !form.icon"
                                />
                            </FormValidator>

                            <b-form-group class="width-40 flex-shrink-0 mt-auto">
                                <ImageLoader v-if="form.icon && form.iconVersion" :src="getIconPath()" :size="40" />
                            </b-form-group>
                        </div>
                    </div>

                    <FormValidator id="form-group-ga-id" :validator="$v.form.gaId" v-slot="slotProps" form-group :label="$t('googleAnalyticsOptional')">
                        <b-form-input id="form-ga-id" v-model="form.gaId" :state="slotProps.state" :disabled="busy" placeholder="G-XXXXXXXXXX" />
                    </FormValidator>

                    <FormValidator id="form-group-source-url" :label="$t('webhookUrlOptional')" :validator="$v.formSpace.webhook" v-slot="slotProps" form-group>
                        <div class="d-flex gap-3">
                            <b-form-input
                                id="form-icon-version"
                                v-model="formSpace.webhook"
                                :state="slotProps.state"
                                placeholder="https://"
                                class=""
                                :disabled="busy"
                            />
                        </div>
                    </FormValidator>

                    <FormValidator id="form-group-display-recommended-links" :validator="$v.form.displayRecommendedLinks" v-slot="slotProps" form-group>
                        <div class="d-flex gap-3">
                            <b-form-checkbox id="form-display-recommended-links" v-model="form.displayRecommendedLinks" :state="slotProps.state" class="my-auto" :disabled="busy">
                                {{ $t('displayRecommendedLinks') }}
                            </b-form-checkbox>
                        </div>
                    </FormValidator>

                    <template v-if="form.displayRecommendedLinks">
                        <FormValidator
                            id="form-group-recommended-links-header"
                            :label="$t('recommendedLinksHeader')"
                            :validator="$v.form.recommendedLinksHeader"
                            v-slot="slotProps"
                            form-group
                        >
                            <div class="d-flex gap-3">
                                <b-form-input id="form-recommended-links-header" v-model="form.recommendedLinksHeader" :state="slotProps.state" class="" :disabled="busy" />
                            </div>
                        </FormValidator>

                        <b-form-group id="form-group-recommended-links" :label="$t('recommendedLinks')">
                            <div class="d-flex flex-column gap-3">
                                <div v-for="(item, index) in form.recommendedLinks" :key="index" class="d-flex gap-3">
                                    <FormValidator
                                        :id="`form-group-recommended-links-${index}-url`"
                                        class="d-flex flex-column flex-grow-2 flex-shrink-0 flex-basis-0"
                                        :validator="$v.form[`recommendedLinks-${index}-url`]"
                                        v-slot="slotProps"
                                    >
                                        <b-form-input
                                            :id="`form-recommended-links-${index}-url`"
                                            v-model="form.recommendedLinks[index].url"
                                            :placeholder="$t('urlAddress')"
                                            :disabled="busy"
                                            :state="slotProps.state"
                                        />
                                    </FormValidator>
                                    <FormValidator
                                        :id="`form-group-recommended-links-${index}-title`"
                                        class="d-flex flex-column flex-grow-1 flex-shrink-0 flex-basis-0"
                                        :validator="$v.form[`recommendedLinks-${index}-title`]"
                                        v-slot="slotProps"
                                    >
                                        <b-form-input
                                            :id="`form-recommended-links-${index}-title`"
                                            v-model="form.recommendedLinks[index].title"
                                            :placeholder="$t('title')"
                                            :disabled="busy"
                                            :state="slotProps.state"
                                        />
                                    </FormValidator>
                                    <FormValidator
                                        :id="`form-group-recommended-links-${index}-image`"
                                        class="d-flex flex-column flex-grow-1 flex-shrink-0 flex-basis-0"
                                        :validator="$v.form[`recommendedLinks-${index}-image`]"
                                        v-slot="slotProps"
                                    >
                                        <b-form-input
                                            :id="`form-recommended-links-${index}-image`"
                                            v-model="form.recommendedLinks[index].image"
                                            :placeholder="$t('imageUrl')"
                                            :disabled="busy"
                                            :state="slotProps.state"
                                        />
                                    </FormValidator>
                                    <ButtonIcon
                                        :disabled="busy || form.recommendedLinks.length <= 1"
                                        variant="danger"
                                        icon="trash-2"
                                        :title="$t('remove')"
                                        class="mb-auto"
                                        @click="removeRecommendedLink(index)"
                                    />
                                </div>
                                <ButtonIcon :disabled="busy || form.recommendedLinks.length >= 4" class="wide mr-auto" variant="outline-success" @click="addRecommendedLink()">
                                    {{ $t('addAnotherLink') }}
                                </ButtonIcon>
                            </div>
                        </b-form-group>
                    </template>

                    <FormValidator id="form-group-display-recommended-products" :validator="$v.form.displayRecommendedProducts" v-slot="slotProps" form-group>
                        <div class="d-flex gap-3">
                            <b-form-checkbox
                                id="form-display-recommended-products"
                                v-model="form.displayRecommendedProducts"
                                :state="slotProps.state"
                                class="my-auto"
                                :disabled="busy"
                            >
                                {{ $t('displayRecommendedProducts') }}
                            </b-form-checkbox>
                        </div>
                    </FormValidator>

                    <template v-if="form.displayRecommendedProducts">
                        <FormValidator
                            id="form-group-recommended-products-header"
                            :label="$t('recommendedProductsHeader')"
                            :validator="$v.form.recommendedProductsHeader"
                            v-slot="slotProps"
                            form-group
                        >
                            <div class="d-flex gap-3">
                                <b-form-input id="form-recommended-products-header" v-model="form.recommendedProductsHeader" :state="slotProps.state" class="" :disabled="busy" />
                            </div>
                        </FormValidator>

                        <b-form-group id="form-group-recommended-products" :label="$t('recommendedProducts')">
                            <div class="d-flex flex-column gap-3">
                                <div v-for="(item, index) in form.recommendedProducts" :key="index" class="d-flex flex-column gap-3">
                                    <ProductSearch
                                        v-if="!form.recommendedProducts[index].ean"
                                        :id="`form-recommended-products-${index}-product-search`"
                                        v-model="form.recommendedProducts[index].product"
                                        :as-select="true"
                                        :placeholder="$t('searchForProduct')"
                                        :disabled="busy"
                                        class="flex-grow-1 flex-basis-0"
                                    />

                                    <div class="d-flex gap-3">
                                        <FormValidator
                                            :id="`form-group-recommended-products-${index}-ean`"
                                            class="d-flex flex-column flex-grow-2 flex-shrink-0 flex-basis-0"
                                            :validator="$v.form[`recommendedProducts-${index}-ean`]"
                                            v-slot="slotProps"
                                        >
                                            <b-form-input
                                                :id="`form-recommended-products-${index}-ean`"
                                                v-model="form.recommendedProducts[index].ean"
                                                :placeholder="$t('ean')"
                                                :disabled="busy"
                                                :state="slotProps.state"
                                            />
                                        </FormValidator>

                                        <FormValidator
                                            :id="`form-group-recommended-products-${index}-name`"
                                            class="d-flex flex-column flex-grow-1 flex-shrink-0 flex-basis-0"
                                            :validator="$v.form[`recommendedProducts-${index}-name`]"
                                            v-slot="slotProps"
                                        >
                                            <b-form-input
                                                :id="`form-recommended-products-${index}-name`"
                                                v-model="form.recommendedProducts[index].name"
                                                :placeholder="$t('name')"
                                                :disabled="busy"
                                                :state="slotProps.state"
                                            />
                                        </FormValidator>
                                        <FormValidator
                                            :id="`form-group-recommended-products-${index}-info`"
                                            class="d-flex flex-column flex-grow-1 flex-shrink-0 flex-basis-0"
                                            :validator="$v.form[`recommendedProducts-${index}-info`]"
                                            v-slot="slotProps"
                                        >
                                            <b-form-input
                                                :id="`form-recommended-products-${index}-info`"
                                                v-model="form.recommendedProducts[index].info"
                                                :placeholder="$t('description')"
                                                :disabled="busy"
                                                :state="slotProps.state"
                                            />
                                        </FormValidator>
                                        <FormValidator
                                            :id="`form-group-recommended-products-${index}-image`"
                                            class="d-flex flex-column flex-grow-1 flex-shrink-0 flex-basis-0"
                                            :validator="$v.form[`recommendedProducts-${index}-image`]"
                                            v-slot="slotProps"
                                        >
                                            <b-form-input
                                                :id="`form-recommended-products-${index}-image`"
                                                v-model="form.recommendedProducts[index].image"
                                                :placeholder="$t('imageUrl')"
                                                :disabled="busy"
                                                :state="slotProps.state"
                                            />
                                        </FormValidator>
                                        <ButtonIcon
                                            variant="danger"
                                            icon="trash-2"
                                            :title="$t('remove')"
                                            class="mb-auto"
                                            :disabled="busy || form.recommendedProducts.length <= 1"
                                            @click="removeRecommendedProduct(index)"
                                        />
                                    </div>
                                </div>
                                <ButtonIcon
                                    :disabled="busy || form.recommendedProducts.length >= 6"
                                    class="wide mr-auto"
                                    variant="outline-success"
                                    @click="addRecommendedProduct()"
                                >
                                    {{ $t('addAnotherProduct') }}
                                </ButtonIcon>
                            </div>
                        </b-form-group>
                    </template>

                    <FormValidator id="form-group-amounts-header" :label="$t('amountsHeader')" :validator="$v.form.amountsHeader" v-slot="slotProps" form-group>
                        <div class="d-flex gap-3">
                            <b-form-input id="form-amounts-header" v-model="form.amountsHeader" :state="slotProps.state" class="" :disabled="busy" />
                        </div>
                    </FormValidator>
                </div>
            </div>

            <div class="d-flex gap-3 mt-5">
                <ButtonIcon :loading="saving" :disabled="busy" class="wide" @click="formSubmit()">
                    {{ $t('save') }}
                </ButtonIcon>
                <ButtonIcon
                    v-if="landingPage"
                    :href="profileUrl"
                    target="_blank"
                    variant="success"
                    icon="external-link"
                    flip-icon
                    :disabled="busy"
                >
                    {{ $t('seeYourProfile') }}
                </ButtonIcon>
            </div>
        </Card>

        <Card class="mt-4">
            <h5 class="mb-3">{{ $t('qrGeneratorTitle') }}</h5>

            <div class="row">
                <div class="col-12 col-lg-6">
                    <b-form-group :label="$t('addYourOwnParametersOptional')">
                        <div class="d-flex flex-wrap gap-2">
                            <FormValidator id="form-group-p1" :validator="$v.formTracking.p1" v-slot="slotProps" class="flex-grow-1 flex-basis-0 width-min-100">
                                <b-form-input v-model="formTracking.p1" :placeholder="$t('abpar1')" :state="slotProps.state" :disabled="busy" />
                            </FormValidator>
                            <FormValidator id="form-group-p2" :validator="$v.formTracking.p2" v-slot="slotProps" class="flex-grow-1 flex-basis-0 width-min-100">
                                <b-form-input v-model="formTracking.p2" :placeholder="$t('abpar2')" :state="slotProps.state" :disabled="busy" />
                            </FormValidator>
                            <FormValidator id="form-group-p3" :validator="$v.formTracking.p3" v-slot="slotProps" class="flex-grow-1 flex-basis-0 width-min-100">
                                <b-form-input v-model="formTracking.p3" :placeholder="$t('abpar3')" :state="slotProps.state" :disabled="busy" />
                            </FormValidator>
                        </div>
                        <small class="text-muted d-block mt-1">{{ $t('qrParamsHint') }}</small>
                    </b-form-group>

                    <b-form-group :label="$t('finalUrl')">
                        <b-input-group>
                            <b-form-input :value="profileUrlWithParams" readonly />
                            <b-input-group-append>
                                <b-button :disabled="!profileUrlWithParams || isSlugDirty" variant="outline-primary" @click="copyProfileUrl">
                                    {{ $t('copy') }}
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>

                    <div class="d-flex gap-2 mt-2">
                        <b-button variant="primary" :disabled="!qr || isSlugDirty || $v.formTracking.$invalid" @click="downloadQrSvg">{{ $t('download') }}</b-button>
                    </div>
                </div>

                <div class="col-12 col-lg-6 d-flex flex-column align-items-center">
                    <div class="mb-2 text-muted small">{{ $t('preview') }}</div>
                    <div class="position-relative d-inline-block" style="width: 240px; height: 240px">
                        <div ref="qrContainer" class="w-100 h-100" :class="{ 'qr-dimmed': isSlugDirty || $v.formTracking.$invalid }"></div>
                        <div v-if="isSlugDirty" class="position-absolute" style="top: 50%; left: 50%; transform: translate(-50%, -50%); max-width: 210px">
                            <small class="d-block text-muted text-center">
                                {{ $t('saveProfileToUpdateQr') }}
                            </small>
                        </div>
                    </div>
                    <small class="text-muted mt-2">
                        {{ $t('qrBrandingHint') }}
                    </small>
                </div>
            </div>
        </Card>
    </div>
</template>

<script>
    import { serialize as objectToFormData } from 'object-to-formdata';
    import cloneDeep from 'lodash/cloneDeep';
    import { required, requiredIf, decimal, minValue, maxValue, helpers, maxLength } from 'vuelidate/lib/validators';
    import { deepMergeWithFilter, scrollToElement } from '@/utils/util';
    import { image, url, fileSize } from '@/utils/validators';
    import FormValidator from '@/components/common/FormValidator.vue';
    import FormFile from '@/components/common/FormFile.vue';
    import ProductSearch from '@/components/common/ProductSearch.vue';
    import ImageLoader from '@/components/common/ImageLoader.vue';
    import ColorField from '@/components/common/ColorField.vue';
    import formBackendValidationMixin from '@/mixins/formBackendValidationMixin';
    import QRCodeStyling from 'qr-code-styling';

    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

    const FORM_DEFAULT_DATA = Object.freeze({
        amounts: [null, null, null],
        profileName: '',
        description: '',
        thanks: '',
        slug: '',
        color: '',
        icon: null,
        iconVersion: null,
        links: [{ url: '', social: '' }],
        gaId: '',
        displayRecommendedLinks: false,
        recommendedLinksHeader: '',
        recommendedLinks: [],
        displayRecommendedProducts: false,
        recommendedProductsHeader: '',
        recommendedProducts: [],
        video: '',
        thanksVideo: '',
        amountsHeader: '',
        displayVideo: false,
    });

    const FORM_AVATAR_DEFAULT_DATA = Object.freeze({
        avatar: null,
    });

    const FORM_SPACE_DEFAULT_DATA = Object.freeze({
        webhook: null,
    });

    const FORM_TRACKING_DEFAULT_DATA = Object.freeze({
        p1: '',
        p2: '',
        p3: '',
    });

    export default {
        name: 'TipboxLinkEdit',
        mixins: [formBackendValidationMixin],
        components: {
            FormValidator,
            FormFile,
            ImageLoader,
            ColorField,
            ProductSearch,
        },
        data() {
            return {
                requestTipboxLandingPage: this.$req('tipbox/TipboxLandingPage'),
                requestTipboxLandingPageAvatar: this.$req('tipbox/TipboxLandingPageAvatar'),
                requestIcons: this.$req('tipbox/Icons'),
                requestIconVersions: this.$req('tipbox/IconVersions'),
                requestAvailableSocials: this.$req('tipbox/AvailableSocials'),
                requestSpace: this.$req('space/space'),
                spaceId: Number.parseInt(this.$route.params.id),
                form: cloneDeep(FORM_DEFAULT_DATA),
                formAvatar: cloneDeep(FORM_AVATAR_DEFAULT_DATA),
                formSpace: cloneDeep(FORM_SPACE_DEFAULT_DATA),
                formTracking: cloneDeep(FORM_TRACKING_DEFAULT_DATA),
                landingPage: null,
                qr: null,
            };
        },
        computed: {
            isSlugDirty() {
                return this.form?.slug !== this.landingPage?.slug;
            },
            formValidations() {
                return {
                    form: {
                        amounts: {
                            0: { required, decimal, minValue: minValue(this.amounts.min[0]), maxValue: maxValue(this.amounts.max[0]) },
                            1: { required, decimal, minValue: minValue(this.amounts.min[1]), maxValue: maxValue(this.amounts.max[1]) },
                            2: { required, decimal, minValue: minValue(this.amounts.min[2]), maxValue: maxValue(this.amounts.max[2]) },
                        },
                        profileName: { required, maxLength: maxLength(50) },
                        description: { maxLength: maxLength(300) },
                        thanks: { maxLength: maxLength(300) },
                        slug: { required, maxLength: maxLength(50), regex: value => (!value ? true : slugRegex.test(value)) },
                        gaId: { maxLength: maxLength(50) },
                        ...this.form.links.reduce(
                            (acc, item, index) => ({
                                ...acc,
                                ...{
                                    [`links-${index}-social`]: {
                                        required: () => (item.url ? required(item.social) : true),
                                    },
                                    [`links-${index}-url`]: {
                                        required: () => (item.social ? required(item.url) : true),
                                        url: () => (item.url ? url(item.url) : true),
                                    },
                                },
                            }),
                            {}
                        ),
                        icon: {},
                        iconVersion: { required: requiredIf(form => form.icon) },
                        video: { url: val => !val || url(val) },
                        thanksVideo: { url: val => !val || url(val) },
                        displayVideo: {},
                        displayRecommendedLinks: {},
                        amountsHeader: {},
                        recommendedLinksHeader: {},
                        ...this.form.recommendedLinks.reduce((acc, item, index) => {
                            const anyFilled = !!(item.title || item.url || item.image);
                            return {
                                ...acc,
                                [`recommendedLinks-${index}-title`]: {
                                    required: () => (this.form.displayRecommendedLinks || anyFilled ? required(item.title) : true),
                                    maxLength: maxLength(100),
                                },
                                [`recommendedLinks-${index}-url`]: {
                                    required: () => (this.form.displayRecommendedLinks || anyFilled ? required(item.url) : true),
                                    url: () => (item.url ? url(item.url) : true),
                                },
                                [`recommendedLinks-${index}-image`]: {
                                    url: () => (item.image ? url(item.image) : true),
                                },
                            };
                        }, {}),
                        displayRecommendedProducts: {},
                        recommendedProductsHeader: {},
                        ...this.form.recommendedProducts.reduce((acc, item, index) => {
                            const anyFilled = !!(item.ean || item.name || item.info || item.image);
                            return {
                                ...acc,
                                [`recommendedProducts-${index}-ean`]: {
                                    required: () => (this.form.displayRecommendedProducts || anyFilled ? required(item.ean) : true),
                                    regex: helpers.regex('ean', /^\d{8}$|^\d{13}$/),
                                },
                                [`recommendedProducts-${index}-name`]: {
                                    required: () => (this.form.displayRecommendedProducts || anyFilled ? required(item.name) : true),
                                    maxLength: maxLength(100),
                                },
                                [`recommendedProducts-${index}-info`]: {
                                    required: () => (this.form.displayRecommendedProducts || anyFilled ? required(item.info) : true),
                                    maxLength: maxLength(300),
                                },
                                [`recommendedProducts-${index}-image`]: {
                                    url: () => (item.image ? url(item.image) : true),
                                },
                            };
                        }, {}),
                    },
                    formAvatar: {
                        avatar: { image: value => !helpers.req(value) || image(value), fileSizeMax: fileSize({ max: '1MB' }) },
                    },
                    formSpace: {
                        webhook: { url: val => !val || url(val) },
                    },
                    formTracking: {
                        p1: { maxLength: maxLength(32) },
                        p2: { maxLength: maxLength(32) },
                        p3: { maxLength: maxLength(32) },
                    },
                };
            },
            parentRoute() {
                return this.$route.params.parentUrl ? this.$router.resolve(this.$route.params.parentUrl).resolved : this.$route.matched.slice(-2)[0];
            },
            profileUrl() {
                if (!this.landingPage?.slug) return '';
                const baseUrl = process.env.VUE_APP_TIPBOX_LANDING_PREVIEW_URL.replace(/\/+$/, '');
                return `${baseUrl}/${this.landingPage.slug}`;
            },
            amounts() {
                const min = 5;
                const max = 1500;
                const step = 0.5;
                const amounts = this.form.amounts;
                const indexes = [0, 1, 2];
                const calculateMin = index => (index === 0 ? min : Math.min(max, (Number.parseInt(amounts[index - 1]) || min) + step));
                const calculateMax = index => max - (indexes.length - index - 1) * step;
                const minValues = Object.fromEntries(indexes.map(index => [index, calculateMin(index)]));
                const maxValues = Object.fromEntries(indexes.map(index => [index, calculateMax(index)]));
                return { min: minValues, max: maxValues };
            },
            loading() {
                return this.requestTipboxLandingPage.getLoading();
            },
            saving() {
                return this.requestTipboxLandingPage.getLoading('patch') || this.requestTipboxLandingPage.getLoading('post');
            },
            error() {
                return this.requestTipboxLandingPage.getError();
            },
            busy() {
                return this.requestTipboxLandingPage.getAnyLoading() || this.requestSpace.getLoading();
            },
            socials() {
                return this.requestAvailableSocials.getData() || [];
            },
            optionsIcons() {
                return (this.requestIcons.getData() || []).sort((a, b) => a.label > b.label);
            },
            optionsIconVersions() {
                return this.requestIconVersions.getData() || [];
            },
            profileUrlWithParams() {
                const base = this.profileUrl || '';
                if (!base) return '';
                const params = new URLSearchParams();
                if (this.formTracking.p1) params.set('p1', String(this.formTracking.p1));
                if (this.formTracking.p2) params.set('p2', String(this.formTracking.p2));
                if (this.formTracking.p3) params.set('p3', String(this.formTracking.p3));
                const qs = params.toString();
                return qs ? `${base}?${qs}` : base;
            },
        },
        watch: {
            profileUrlWithParams() {
                this.updateQr();
            },
            'form.color': {
                handler() {
                    this.updateQr();
                },
            },
            'form.iconVersion': {
                handler() {
                    this.updateQr();
                },
            },
            'form.recommendedProducts': {
                handler(newVal) {
                    newVal.forEach((item, index) => {
                        if (item.product) {
                            if (item.product.eans && item.product.eans.length > 0 && !item.ean) {
                                this.form.recommendedProducts[index].ean = item.product.eans[0];
                            }
                            if (item.product.name && !item.name) {
                                this.form.recommendedProducts[index].name = item.product.name;
                            }
                            if (item.product.info && !item.info) {
                                this.form.recommendedProducts[index].info = item.product.info;
                            }
                            if (item.product.image && !item.image) {
                                this.form.recommendedProducts[index].image = item.product.image;
                            }
                            delete this.form.recommendedProducts[index].product;
                        }
                    });
                },
                deep: true,
            },
            landingPage: {
                handler() {
                    this.updateQr();
                },
                deep: true,
            },
            'form.icon': {
                handler(icon, prevIcon) {
                    if (!icon) {
                        this.form.iconVersion = null;
                    } else if (!prevIcon) {
                        this.form.iconVersion = 'colorful';
                    }
                    this.updateQr();
                },
            },
            'formAvatar.avatar': {
                handler(avatar) {
                    if (avatar) this.$v.formAvatar.avatar.$touch();
                },
            },
            'formSpace.webhook': {
                handler(webhook) {
                    if (webhook) this.$v.formSpace.webhook.$touch();
                },
            },

            'form.displayRecommendedLinks': {
                handler(display) {
                    if (display && this.form.recommendedLinks.length === 0) {
                        this.addRecommendedLink();
                    }
                },
            },

            'form.displayRecommendedProducts': {
                handler(display) {
                    if (display && this.form.recommendedProducts.length === 0) {
                        this.addRecommendedProduct();
                    }
                },
            },
        },
        methods: {
            initFormData() {
                const form = deepMergeWithFilter(FORM_DEFAULT_DATA, this.landingPage);
                this.form = form;
            },
            initFormAvatarData() {
                this.formAvatar = deepMergeWithFilter(FORM_AVATAR_DEFAULT_DATA, {});
            },
            initFormSpaceData() {
                this.formSpace = deepMergeWithFilter(FORM_SPACE_DEFAULT_DATA, this.requestSpace.getData() || {});
            },
            fetchTipboxLandingPage() {
                return this.requestTipboxLandingPage
                    .get({ urlParams: { spaceId: this.spaceId } })
                    .then(data => {
                        this.landingPage = { ...this.landingPage, ...data };
                        this.initFormData();
                    })
                    .catch(() => {});
            },
            getIconPath() {
            return require(`@/assets/ikonki_tipbox/${this.form.icon}/${this.form.iconVersion}.svg`); // eslint-disable-line
            },
            fetchIcons() {
                return this.requestIcons.get().catch(() => {});
            },
            fetchIconVersions() {
                return this.requestIconVersions.get().catch(() => {});
            },
            fetchAvailableSocials() {
                return this.requestAvailableSocials.get().catch(() => {});
            },
            fetchSpace() {
                return this.requestSpace
                    .get(this.spaceId)
                    .then(this.initFormSpaceData)
                    .catch(() => {});
            },
            async formSubmit() {
                this.$v.form.$touch();
                this.$v.formAvatar.$touch();
                this.$v.formSpace.$touch();
                if (this.$v.form.$invalid || this.$v.formAvatar.$invalid || this.$v.formSpace.$invalid) {
                    await this.$nextTick();
                    scrollToElement('.is-invalid', { offset: -80 });
                    return;
                }
                await this.saveForm();
                await this.saveAvatar();
                await this.saveSpace();
            },
            saveForm() {
                if (!this.form.recommendedProducts.length) {
                    delete this.form.recommendedProducts;
                }
                if (!this.form.recommendedLinks.length) {
                    delete this.form.recommendedLinks;
                }
                const apiParams = {
                    urlParams: { spaceId: this.spaceId },
                    requestParams: {
                        ...this.form,
                    links: this.form.links.filter(({ url, social }) => url && social), // eslint-disable-line
                    },
                };
                const type = this.landingPage ? 'patch' : 'post';
                return this.requestTipboxLandingPage
                    .id(type)
                    [type](apiParams)
                    .then(data => {
                        this.landingPage = { ...this.landingPage, ...this.form, ...data };
                        this.initFormData();
                        this.$toastr.success(this.$t('dataSavedSuccessfully'));
                        this.$v.form.$reset();
                    })
                    .catch(this.handleBackendValidations);
            },
            ensureQrInstance() {
                if (!this.qr) {
                    this.qr = new QRCodeStyling({
                        width: 240,
                        height: 240,
                        type: 'svg',
                        data: this.profileUrlWithParams || 'about:blank',
                        margin: 8,
                        qrOptions: { errorCorrectionLevel: 'Q' },
                        dotsOptions: { type: 'rounded', color: this.form.color },
                        backgroundOptions: { color: 'transparent' },
                        imageOptions: { crossOrigin: 'anonymous', margin: 3 },
                    });
                }
            },
            updateQr() {
                if (!this.qr) return;
                this.$v.formTracking.$touch();

                const payload = {
                    data: this.profileUrlWithParams || 'about:blank',
                };

                payload.dotsOptions = { type: 'rounded', color: this.form.color || '#000000' };
                payload.backgroundOptions = { color: 'transparent' };

                if (this.form.icon && this.form.iconVersion) {
                    payload.image = this.getIconPath();
                    payload.imageOptions = { crossOrigin: 'anonymous', margin: 3, width: 40, height: 40 };
                } else {
                    payload.image = undefined;
                }

                this.qr.update(payload);
            },
            downloadQrSvg() {
                if (!this.qr) return;

                this.qr.download({
                    name: `tipbox-qr-${this.form.slug}`,
                    extension: 'svg',
                });
            },
            async copyProfileUrl() {
                try {
                    await this.$copyText(this.profileUrlWithParams);
                    this.$toastr.success(this.$t('copied'));
                } catch {
                    this.$toastr.error(this.$t('errorOccurred'));
                }
            },
            saveAvatar() {
                if (!this.formAvatar.avatar) {
                    return Promise.resolve();
                }
                const apiParams = {
                    urlParams: { spaceId: this.spaceId },
                    requestParams: objectToFormData(this.formAvatar),
                    headers: { 'Content-Type': 'multipart/form-data' },
                };
                return this.requestTipboxLandingPageAvatar
                    .post(apiParams)
                    .then(({ avatar }) => {
                        if (avatar) this.$set(this.landingPage, 'avatar', avatar);
                        this.initFormAvatarData();
                        this.$v.formAvatar.$reset();
                    })
                    .catch(error => {
                        if (error.messages.length) {
                            this.$toastr.error(error.messages, error.message);
                        } else {
                            this.$toastr.error(error.message, this.$t('errorOccurred'));
                        }
                    });
            },
            saveSpace() {
                return this.requestSpace
                    .patch(this.spaceId, { webhook: this.formSpace.webhook })
                    .then(({ webhook }) => {
                        // if (webhook) this.$set(this.landingPage, 'webhook', webhook);
                        this.initFormSpaceData();
                        this.$v.formSpace.$reset();
                    })
                    .catch(error => {
                        if (error.messages.length) {
                            this.$toastr.error(error.messages, error.message);
                        } else {
                            this.$toastr.error(error.message, this.$t('errorOccurred'));
                        }
                    });
            },
            addLink() {
                this.form.links.push({ url: '', social: '' });
            },
            removeLink(index) {
                this.form.links.splice(index, 1);
            },
            addRecommendedLink() {
                if (this.form.recommendedLinks.length >= 4) return;

                this.form.recommendedLinks.push({ url: '', title: '', image: '' });
            },
            removeRecommendedLink(index) {
                if (this.form.recommendedLinks.length <= 1) return;

                this.form.recommendedLinks.splice(index, 1);
            },
            addRecommendedProduct() {
                if (this.form.recommendedProducts.length >= 6) return;
                this.form.recommendedProducts.push({ ean: '', name: '', info: '', image: '' });
            },
            removeRecommendedProduct(index) {
                if (this.form.recommendedProducts.length <= 1) return;

                this.form.recommendedProducts.splice(index, 1);
            },
        },
        created() {
            this.fetchTipboxLandingPage();
            this.fetchIcons();
            this.fetchIconVersions();
            this.fetchAvailableSocials();
            this.fetchSpace();
        },
        mounted() {
            this.ensureQrInstance();
            this.$nextTick(() => {
                if (this.$refs.qrContainer && this.qr) {
                    this.qr.append(this.$refs.qrContainer);
                    this.updateQr();
                }
            });
        },
    };
</script>

<style lang="scss" scoped>
    .qr-dimmed {
        filter: blur(4px);
        opacity: 0.1;
    }
</style>
